/**
 * Call speech out: local system voices only (a voice with `localService === true`; Chrome's
 * "Google …" voices are remote and never used), chunk by chunk as the daemon streams them, with
 * interruption tracking: on a barge-in the speaker reports exactly what was heard, the word it
 * was cut at, and what was never spoken (spec: "Interruption tracking").
 */
import type { CallInterruption } from '@viko/protocol'

export interface VoiceLike {
  name: string
  lang: string
  localService: boolean
  default?: boolean
}

/** The best local voice for `lang`, or undefined (then the call shows captions only). */
export function pickLocalVoice<V extends VoiceLike>(
  voices: readonly V[],
  lang: string,
): V | undefined {
  const local = voices.filter((v) => v.localService === true)
  const l = lang.toLowerCase()
  const base = l.split('-')[0]!
  return (
    local.find((v) => v.lang.toLowerCase() === l && v.default) ??
    local.find((v) => v.lang.toLowerCase() === l && /premium|enhanced/i.test(v.name)) ??
    local.find((v) => v.lang.toLowerCase() === l) ??
    local.find((v) => v.lang.toLowerCase().split(/[-_]/)[0] === base)
  )
}

export interface UtteranceLike {
  text: string
  voice: unknown
  lang: string
  rate: number
  onstart: (() => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
  onboundary: ((e: { charIndex: number; name?: string }) => void) | null
}

export interface SynthLike {
  speak(u: UtteranceLike): void
  cancel(): void
}

interface Chunk {
  turnId: string
  text: string
  state: 'queued' | 'speaking' | 'done'
  /** Character index of the word being spoken (boundary events). */
  at: number
}

export class CallSpeaker {
  private chunks: Chunk[] = []
  private cutTurn: { turnId: string; extra: string[] } | undefined
  constructor(
    private readonly o: {
      synth: SynthLike
      make: (text: string) => UtteranceLike
      voice: () => VoiceLike | undefined
      lang: string
      rate?: number
      onSpeaking?: (on: boolean) => void
    },
  ) {}

  get speaking(): boolean {
    return this.chunks.some((c) => c.state !== 'done')
  }

  /** Whether speech can play at all (a local voice exists). */
  get audible(): boolean {
    return !!this.o.voice()
  }

  /** Text just spoken (the echo guard compares what the mic hears with it). */
  recentText(): string {
    return this.chunks
      .filter((c) => c.state !== 'queued')
      .slice(-3)
      .map((c) => c.text)
      .join(' ')
  }

  say(turnId: string, text: string): void {
    if (!text.trim()) return
    // Chunks of a turn the user cut off are never spoken; they're reported as unheard.
    if (this.cutTurn?.turnId === turnId) {
      this.cutTurn.extra.push(text)
      return
    }
    const voice = this.o.voice()
    if (!voice) return
    const chunk: Chunk = { turnId, text, state: 'queued', at: 0 }
    this.chunks.push(chunk)
    const u = this.o.make(text)
    u.voice = voice
    u.lang = voice.lang || this.o.lang
    u.rate = this.o.rate ?? 1.05
    u.onstart = () => {
      chunk.state = 'speaking'
      this.o.onSpeaking?.(true)
    }
    u.onboundary = (e) => {
      chunk.at = e.charIndex
    }
    const settle = () => {
      chunk.state = 'done'
      chunk.at = chunk.text.length
      this.chunks = this.chunks.filter((c) => c.state !== 'done' || c.turnId === turnId)
      if (!this.speaking) this.o.onSpeaking?.(false)
    }
    u.onend = settle
    u.onerror = settle
    this.o.synth.speak(u)
  }

  /**
   * Barge-in: stop at once and say where. Returns undefined when nothing was being said. Later
   * chunks of the same turn are swallowed and added to `unheard` (see `takeCut`).
   */
  cut(): CallInterruption | undefined {
    const turnId = this.chunks.find((c) => c.state !== 'done')?.turnId
    if (!turnId) {
      this.o.synth.cancel()
      return undefined
    }
    const turn = this.chunks.filter((c) => c.turnId === turnId)
    const heard = turn.filter((c) => c.state === 'done').map((c) => c.text)
    const speaking = turn.find((c) => c.state === 'speaking')
    let cutAt = ''
    let rest = ''
    if (speaking) {
      // Up to the end of the word in progress at the last boundary.
      const end = speaking.text.indexOf(' ', speaking.at + 1)
      const stop = end < 0 ? speaking.text.length : end
      cutAt = speaking.text.slice(0, stop).trim()
      rest = speaking.text.slice(stop).trim()
    }
    const unheard = [rest, ...turn.filter((c) => c.state === 'queued').map((c) => c.text)]
      .filter(Boolean)
      .join(' ')
    this.cutTurn = { turnId, extra: [] }
    this.chunks = []
    this.o.synth.cancel()
    this.o.onSpeaking?.(false)
    return { heard: heard.join(' '), cutAt, unheard, turnId }
  }

  /** Finish a cut: anything that streamed in after it, for the agent's `unheard`. */
  takeCut(c: CallInterruption): CallInterruption {
    const t = this.cutTurn
    const extra = t && t.turnId === c.turnId ? t.extra : []
    return extra.length ? { ...c, unheard: [c.unheard, ...extra].filter(Boolean).join(' ') } : c
  }

  /** A new turn started: stop swallowing. */
  turn(turnId: string): void {
    if (this.cutTurn && this.cutTurn.turnId !== turnId) this.cutTurn = undefined
  }

  stop(): void {
    this.chunks = []
    this.o.synth.cancel()
    this.o.onSpeaking?.(false)
  }
}

/** Is `heard` just the speaker's own voice coming back through the mic? */
export function isEcho(heard: string, spoken: string): boolean {
  const norm = (x: string) =>
    x
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .split(/\s+/)
      .filter(Boolean)
  const h = norm(heard)
  if (h.length < 2) return false
  const s = new Set(norm(spoken))
  const hits = h.filter((w) => s.has(w)).length
  return hits / h.length >= 0.8
}
